import { Component, computed, DestroyRef, effect, inject, input, output, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { DateService } from 'src/app/shared/services/date.service';

import { defaultDayRange } from '../helpers/disease.helper';
import { CropFieldComparator, SortMethodType } from '../helpers/sort.helpers';
import { CropFieldAdvice } from '../models/models';
import { SortMenuComponent } from './sort-menu.component';
import { MatFormField, MatLabel, MatSelect } from '@angular/material/select';
import { MatIcon } from '@angular/material/icon';
import { TranslatePipe } from '@next/core-lib/i18n';
import { DateSelectorComponent } from '../../date-selector.component/date-selector.component';
import { MatOption } from '@angular/material/core';
import { MatButton } from '@angular/material/button';

@Component({
  selector: 'app-disease-overview-table-toolbar',
  styleUrl: '../table.component.scss',
  template: `
    <div class="flex flex--row flex--justify-space-between flex--align-center table-toolbar">
      <div class="flex--row flex--align-top table-toolbar-button-group mt-1">
        <div class="flex--column">
          <button
            mat-flat-button
            color="accent"
            class="action-button"
            [disabled]="amountOfSelectedCropFields() === 0"
            [matMenuTriggerFor]="actionMenu()"
            (menuOpened)="onActionMenuOpened()"
          >
            <mat-icon class="table-toolbar-button-icon action-button-icon">add_task</mat-icon>
            <span class="flex--align-center table-toolbar-button-span">
              {{ 'shared.diseaseTable.actions' | translate }}
              @if (amountOfSelectedCropFields()) {
                <span class="table-toolbar-button-counter">{{ amountOfSelectedCropFields() }}</span>
              }
              <mat-icon>keyboard_arrow_down</mat-icon>
            </span>
          </button>
        </div>
        <mat-form-field class="custom-selector">
          <mat-label>{{ 'shared.diseaseTable.filterCropFields' | translate }}</mat-label>
          <mat-select [formControl]="cropFieldsSelector" multiple>
            @for (cropField of diseaseCropFields(); track cropField.id) {
              <mat-option [value]="cropField.id">{{ cropField.name }} {{ cropField.areaInHectares }} ha</mat-option>
            }
          </mat-select>
        </mat-form-field>
      </div>
      <div class="flex--row flex--align-center column-gap">
        <div class="flex--row flex--align-center">
          <span class="text--bold table-toolbar-button-label">{{ 'shared.diseaseTable.sort.title' | translate }}</span>
          <app-disease-sort-menu
            #sortMenu
            [availableSortMethods]="availableSortMethods()"
            (sortMethodSelected)="onSortChange($event)"
          />
        </div>
        <app-date-selector
          [minimalDate]="minimalDate()"
          [maximalDate]="maximalDate()"
          [selectedDates]="selectedDates()"
          [cropYear]="cropYear()"
          [daysInPast]="defaultDayRange.daysInPast"
          [daysInFuture]="defaultDayRange.daysInFuture"
          (selectedDatesChanges)="onSelectedDatesChanged($event)"
        />
      </div>
    </div>
  `,
  imports: [
    SortMenuComponent,
    MatFormField,
    MatSelect,
    MatIcon,
    MatOption,
    DateSelectorComponent,
    TranslatePipe,
    MatMenuTrigger,
    ReactiveFormsModule,
    MatLabel,
    MatButton,
  ],
})
export class OverviewTableToolbarComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly dateService = inject(DateService);

  private readonly visibleCropFieldIds = signal<Set<number>>(new Set());

  public readonly selectedCropFields = input.required<CropFieldAdvice[]>();
  public readonly diseaseCropFields =
    input.required<{ id: number; name: string; areaInHectares: number; isVisible: boolean }[]>();
  public readonly selectedDates = input<Date[]>([]);
  public readonly cropYear = input<number | undefined>(undefined);
  public readonly minimalDate = input<Date | undefined>(undefined);
  public readonly actionMenu = input.required<MatMenu>();
  public readonly availableSortMethods = input<SortMethodType[]>([
    SortMethodType.Name,
    SortMethodType.Advice,
    SortMethodType.Spray,
  ]);

  protected readonly amountOfSelectedCropFields = computed(() => {
    const visibleIds = this.visibleCropFieldIds();
    return this.selectedCropFields().filter((cf) => visibleIds.has(cf.cropFieldId)).length;
  });
  protected readonly defaultDayRange = defaultDayRange;

  @ViewChild('sortMenu')
  protected sortMenu!: SortMenuComponent;

  public readonly selectedDatesChanges = output<Date[]>();
  public readonly sortMethodChanges = output<CropFieldComparator>();
  public readonly cropFieldVisibilityChanged = output<{ cropFieldId: number; isVisible: boolean }>();
  public readonly selectedCropFieldsChanged = output<CropFieldAdvice[]>();
  public readonly actionMenuOpened = output<void>();

  protected readonly maximalDate = computed(() =>
    this.dateService.daysInTheFuture(defaultDayRange.daysInFuture, this.cropYear()),
  );
  protected readonly cropFieldsSelector = new FormControl<number[]>([], { nonNullable: true });

  constructor() {
    effect(() => {
      const visibleIds = new Set(
        this.diseaseCropFields()
          .filter((cf) => cf.isVisible ?? true)
          .map((cf) => cf.id),
      );

      if (areSetsEqual(new Set(this.cropFieldsSelector.value), visibleIds)) {
        return;
      }

      this.visibleCropFieldIds.set(visibleIds);
      this.cropFieldsSelector.setValue([...visibleIds], { emitEvent: false });
    });

    this.subscribeToCropFieldSelectorChanges();
  }

  protected onSelectedDatesChanged(dates: Date[]): void {
    this.selectedDatesChanges.emit(dates);
  }

  protected onSortChange(sortMethod: CropFieldComparator): void {
    this.sortMethodChanges.emit(sortMethod);
  }

  protected onActionMenuOpened(): void {
    this.actionMenuOpened.emit();
  }

  private subscribeToCropFieldSelectorChanges(): void {
    this.cropFieldsSelector.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        debounceTime(500),
        distinctUntilChanged((prev, curr) => areSetsEqual(new Set(prev), new Set(curr))),
      )
      .subscribe((selectedIds: number[]) => {
        const previousIds = this.visibleCropFieldIds();
        const selectedSet = new Set(selectedIds);

        this.visibleCropFieldIds.set(selectedSet);

        this.selectedCropFieldsChanged.emit(
          this.selectedCropFields().filter((advice) => selectedSet.has(advice.cropFieldId)),
        );

        diffIds(previousIds, selectedSet).forEach((change) => {
          this.cropFieldVisibilityChanged.emit(change);
        });
      });
  }
}

function areSetsEqual(a: Set<number>, b: Set<number>): boolean {
  return a.size === b.size && [...a].every((id) => b.has(id));
}

function diffIds(previous: Set<number>, current: Set<number>): { cropFieldId: number; isVisible: boolean }[] {
  const changes: { cropFieldId: number; isVisible: boolean }[] = [];
  current.forEach((id) => {
    if (!previous.has(id)) {
      changes.push({ cropFieldId: id, isVisible: true });
    }
  });
  previous.forEach((id) => {
    if (!current.has(id)) {
      changes.push({ cropFieldId: id, isVisible: false });
    }
  });
  return changes;
}
